import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Model } from '../api/types';
import Button from './Button';

interface Props {
  model: Model;
  busy: boolean;
  onRename: (id: string, name: string) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onDelete: (id: string) => void;
}

// One model row with inline rename + inline delete-confirm (no native dialogs, so
// the whole flow is testable and Playwright-friendly). The name links to the
// editor; archived models still open — read-only.
export default function ModelRow({ model, busy, onRename, onArchive, onUnarchive, onDelete }: Props) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(model.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function saveRename() {
    const trimmed = name.trim();
    if (trimmed && trimmed !== model.name) onRename(model.id, trimmed);
    setEditing(false);
  }
  function cancelRename() {
    setName(model.name);
    setEditing(false);
  }

  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-[var(--cad-panel-border,#27272a)] bg-[color-mix(in_srgb,var(--cad-panel-bg,#09090b)_40%,transparent)] px-4 py-3">
      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            aria-label="Model name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename();
              if (e.key === 'Escape') cancelRename();
            }}
            className="w-full rounded-md border border-[var(--cad-panel-border,#3f3f46)] bg-[var(--cad-panel-bg,#18181b)] px-2 py-1 text-sm text-[var(--cad-text,#f4f4f5)]"
          />
        ) : (
          // model names are user-controlled — rendered as text, never HTML.
          <Link
            to={`/editor/${model.id}`}
            className="truncate text-sm font-medium text-[var(--cad-text,#f4f4f5)] hover:text-[var(--cad-accent,#a5b4fc)]"
          >
            {model.name}
          </Link>
        )}
        <div className="mt-0.5 text-xs text-[var(--cad-text-muted,#71717a)]">
          Updated {new Date(model.updated_at).toLocaleDateString()}
          {model.archived ? ' · archived' : ''}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {editing ? (
          <>
            <Button variant="secondary" onClick={saveRename}>
              Save
            </Button>
            <Button variant="secondary" onClick={cancelRename}>
              Cancel
            </Button>
          </>
        ) : confirmDelete ? (
          <>
            <span className="text-xs text-[var(--cad-text-muted,#a1a1aa)]">Delete permanently?</span>
            <Button variant="danger" loading={busy} onClick={() => onDelete(model.id)}>
              Yes, delete
            </Button>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              No
            </Button>
          </>
        ) : (
          <>
            {model.archived ? (
              <Button variant="secondary" loading={busy} onClick={() => onUnarchive(model.id)}>
                Unarchive
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Rename
                </Button>
                <Button variant="secondary" loading={busy} onClick={() => onArchive(model.id)}>
                  Archive
                </Button>
              </>
            )}
            <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </>
        )}
      </div>
    </li>
  );
}
